// Adaptador: adminPosts.ts → consume del motor contentMaster.ts
// Exporta las mismas interfaces y datos que esperan las páginas.

import {
  canonicalPosts,
  getPhotoUrl,
  type CanonicalPost,
} from './contentMaster';

export type PostType = 'texto' | 'imagen' | 'video' | 'encuesta' | 'patrocinada';
export type PostStatus = 'publicado' | 'borrador' | 'programado';
export type PollType = 'single' | 'multiple';

export interface PollOption {
  id: number;
  text: string;
  votes: number;
}

export interface PollData {
  pollType: PollType;
  options: PollOption[];
  isClosed: boolean;
}

export interface PostEngagement {
  likes: number;
  comments: number;
  shares: number;
  reach: number;
}

export interface AdminPost {
  id: number;
  title: string;
  text: string;
  type: PostType;
  imageUrl?: string;
  videoUrl?: string;
  scheduledDate?: string;
  status: PostStatus;
  isFeatured: boolean;
  engagement: PostEngagement;
  pollData?: PollData;
  isSponsored: boolean;
  sponsorName?: string;
  createdAt: string;
  updatedAt: string;
  // Lineage
  derivedFromVideoId?: number;
  derivedFromLiveId?: number;
  referencesProgramId?: string;
}

export const postTypeLabels: Record<PostType, string> = {
  texto: 'Texto',
  imagen: 'Imagen',
  video: 'Video',
  encuesta: 'Encuesta',
  patrocinada: 'Patrocinada',
};

export const postTypeIcons: Record<PostType, string> = {
  texto: 'ri-file-text-line',
  imagen: 'ri-image-line',
  video: 'ri-video-line',
  encuesta: 'ri-bar-chart-horizontal-line',
  patrocinada: 'ri-megaphone-line',
};

export const postTypeColors: Record<PostType, string> = {
  texto: 'bg-secondary-100 text-secondary-900',
  imagen: 'bg-accent-100 text-accent-900',
  video: 'bg-accent-100 text-accent-900',
  encuesta: 'bg-amber-100 text-amber-700',
  patrocinada: 'bg-primary-100 text-primary-700',
};

export const postStatusLabels: Record<PostStatus, string> = {
  publicado: 'Publicado',
  borrador: 'Borrador',
  programado: 'Programado',
};

export const postStatusColors: Record<PostStatus, string> = {
  publicado: 'bg-emerald-100 text-emerald-700',
  borrador: 'bg-foreground-100 text-foreground-500',
  programado: 'bg-amber-100 text-amber-700',
};

export const postStatusIcons: Record<PostStatus, string> = {
  publicado: 'ri-check-double-line',
  borrador: 'ri-draft-line',
  programado: 'ri-time-line',
};

// ─── Mapeo ────────────────────────────────────────────────

function toLegacyPostId(canonicalId: string): number {
  return parseInt(canonicalId.split('-')[1], 10);
}

function mapPostType(pt: CanonicalPost['postType']): PostType {
  switch (pt) {
    case 'text': return 'texto';
    case 'image': return 'imagen';
    case 'video': return 'video';
    case 'poll': return 'encuesta';
    case 'sponsored': return 'patrocinada';
    default: return 'texto';
  }
}

function mapPostStatus(s: CanonicalPost['status']): PostStatus {
  if (s === 'published') return 'publicado';
  if (s === 'draft') return 'borrador';
  return 'programado';
}

function mapPost(p: CanonicalPost): AdminPost {
  const legacyType = mapPostType(p.postType);
  return {
    id: toLegacyPostId(p.id),
    title: p.title,
    text: p.text,
    type: legacyType,
    imageUrl: p.imagePhotoId ? getPhotoUrl(p.imagePhotoId) : undefined,
    videoUrl: undefined, // los videos se referencian por ID, no por URL directa
    scheduledDate: p.scheduledAt,
    status: mapPostStatus(p.status),
    isFeatured: p.isFeatured,
    engagement: {
      likes: p.engagement.likes,
      comments: p.engagement.comments,
      shares: p.engagement.shares,
      reach: p.engagement.reach,
    },
    pollData: p.pollData ? {
      pollType: p.pollData.pollType,
      options: p.pollData.options,
      isClosed: p.pollData.isClosed,
    } : undefined,
    isSponsored: p.isSponsored,
    sponsorName: p.sponsorName,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
    derivedFromVideoId: p.derivedFromVideoId ? toLegacyPostId(p.derivedFromVideoId) : undefined,
    derivedFromLiveId: p.derivedFromLiveId ? toLegacyPostId(p.derivedFromLiveId) : undefined,
    referencesProgramId: p.referencesProgramId,
  };
}

export const adminPosts: AdminPost[] = canonicalPosts.map(mapPost);